import { Autocomplete, Stack, TextField } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { PositionRangeAutocomplete, useMinimalsTheme } from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { YardPositionBlockRange } from './models/yard-position-block-range.model'
import { YardPositionRangeSelectorStore } from './stores/yard-position-range-selector.store'

interface Props {
  showTier?: boolean
  position?: YardPositionBlockRange
  hasError?: boolean
  onChange: (destination?: YardPositionBlockRange) => void
}

export const YardPositionRangeSelector = observer(
  ({ onChange, position, hasError, showTier = false }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { yardBlockStore } = useYardStores()
    const yardPositionRangeSelector = useMemo(
      () => new YardPositionRangeSelectorStore(yardBlockStore, position),
      [yardBlockStore],
    )

    useEffect(() => {
      return () => yardPositionRangeSelector.reset()
    }, [yardPositionRangeSelector])

    const handleChange = () => {
      onChange(yardPositionRangeSelector.yardPositionBlockRange)
    }

    return (
      <Stack gap={theme.customSpacing.s} flexDirection='row'>
        <Autocomplete
          options={yardPositionRangeSelector.yardBlocks}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={yardPositionRangeSelector.selectedBlock}
          renderInput={params => (
            <TextField
              {...params}
              label={t('yardBlock', 'Yard block')}
              required
              error={hasError && !yardPositionRangeSelector.selectedBlockId}
              helperText={
                hasError && !yardPositionRangeSelector.selectedBlockId
                  ? t('fieldIsRequired', 'Field is required')
                  : undefined
              }
              data-cy='allocation-destination-block'
            />
          )}
          onChange={(e, value) => {
            yardPositionRangeSelector.setSelectedBlockId(value?.id)
            handleChange()
          }}
          fullWidth
        />

        {yardPositionRangeSelector.isSelectedBlockStandard && (
          <>
            <PositionRangeAutocomplete
              label={t('bay', 'Bay')}
              options={yardPositionRangeSelector.bays}
              handleChange={value => {
                yardPositionRangeSelector.setSelectedBay(value)
                handleChange()
              }}
              value={yardPositionRangeSelector.selectedBay?.split('-') ?? []}
            />
            <PositionRangeAutocomplete
              label={t('row', 'Row')}
              options={yardPositionRangeSelector.rows}
              handleChange={value => {
                yardPositionRangeSelector.setSelectedRow(value)
                handleChange()
              }}
              value={yardPositionRangeSelector.selectedRow?.split('-') ?? []}
            />
            {showTier && (
              <Autocomplete
                options={yardPositionRangeSelector.tiers}
                renderInput={params => <TextField {...params} label={t('tier', 'Tier')} />}
                onChange={(e, value) => {
                  yardPositionRangeSelector.setSelectedTier(value ?? undefined)
                  handleChange()
                }}
                value={yardPositionRangeSelector.selectedTier}
                fullWidth
              />
            )}
          </>
        )}
      </Stack>
    )
  },
)
